// src/components/AuthLoadingBoundary.js
import { useEffect, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'

const AuthLoadingBoundary = ({ children, fallback = null }) => {
  const { loading, authInitialized, authError, user } = useAuth()
  const [timeoutReached, setTimeoutReached] = useState(false)

  // Set a timeout to prevent infinite loading
  useEffect(() => {
    const timer = setTimeout(() => {
      if (loading && !authInitialized) {
        console.log('⏰ Auth loading timeout reached')
        setTimeoutReached(true)
      }
    }, 8000) // 8 second timeout

    return () => clearTimeout(timer)
  }, [loading, authInitialized])

  // Show error state
  if (authError && !loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full mx-4">
          <div className="text-center">
            <div className="h-12 w-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.962-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Authentication Error</h3>
            <p className="text-gray-600 mb-4">{authError}</p>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Show loading state or timeout fallback
  if ((loading && !authInitialized) || (!timeoutReached && !authInitialized)) {
    if (timeoutReached) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full mx-4">
            <div className="text-center">
              <div className="h-12 w-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Taking longer than expected</h3>
              <p className="text-gray-600 mb-4">Authentication is taking longer than usual. This might be a connection issue.</p>
              <div className="space-y-2">
                <button
                  onClick={() => window.location.reload()}
                  className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  Reload Page
                </button>
                <button
                  onClick={() => window.location.href = '/login'}
                  className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Go to Login
                </button>
              </div>
            </div>
          </div>
        </div>
      )
    }

    return fallback || (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading</h2>
          <p className="text-gray-600">Setting up your dashboard...</p>
          <div className="mt-4 text-sm text-gray-500">
            This should only take a moment
          </div>
        </div>
      </div>
    )
  }

  // Auth is ready, render children
  return children
}

export default AuthLoadingBoundary